import React, { useMemo } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import Direction from '@/enums/direction.enum'
import RichTooltipContent from '@/modules/base/components/rich-tooltip/RichTooltipContent'

const useStyles = makeStyles((theme) => ({
  tooltip: {
    maxWidth: 'unset',
    backgroundColor: 'transparent',
    fontSize: theme.fontSizes.default,
  },
  popper: {
    '&[data-popper-placement*="bottom"] $arrowOverride': {
      top: 0,
      left: 0,
      marginTop: 'calc(-0.71em + 4px)',
      marginLeft: 4,
      marginRight: 4,
      '&::before': {
        transformOrigin: '0 100%',
      },
    },
    '&[data-popper-placement*="top"] $arrowOverride': {
      bottom: 0,
      left: 0,
      marginBottom: 'calc(-0.71em + 4px)',
      marginLeft: 4,
      marginRight: 4,
      '&::before': {
        transformOrigin: '100% 0',
      },
    },
    '&[data-popper-placement*="right"] $arrowOverride': {
      left: 4,
      marginLeft: 'calc(-0.71em + 4px)',
      height: '1em',
      width: '0.71em',
      marginTop: 4,
      marginBottom: 4,
      '&::before': {
        transformOrigin: '100% 100%',
      },
    },
    '&[data-popper-placement*="left"] $arrowOverride': {
      right: 0,
      marginRight: 'calc(-0.71em + 4px)',
      height: '1em',
      width: '0.71em',
      marginTop: 4,
      marginBottom: 4,
      '&::before': {
        transformOrigin: '0 0',
      },
    },
  },
  content: ({ padding }) => ({
    padding: padding || 20,
    color: theme.text,
  }),
  tooltipContainer: {
    display: 'flex',
    position: 'relative',
    width: ({ width }) => width,
    zIndex: 2,
    boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.30)',
    borderRadius: 4,
    background: theme.white,
  },
  arrowOverride: {
    zIndex: '2',
    color: theme.white,
    '&::before': {
      boxShadow: '0 0 6px 0 rgba(0, 0, 0, 0.30)',
    },
  },
}))

const RichTooltip = (props) => {
  const {
    children,
    content,
    heading,
    body,
    placement = Direction.RIGHT,
    disabled,
    offsetX,
    offsetY,
    onOpen = () => {},
    styles = {},
    hideArrow,
    padding,
    width = 320,
    enterDelay = 500,
    enterTouchDelay = 700,
    open,
    popperModifiers,
  } = props

  // Given `open`, the caller decides when the tooltip shows; hovering the anchor
  // must not open it on its own.
  const isControlled = open !== undefined

  const classes = useStyles({ width, padding })

  const popperProps = { style: {}, container: document.fullscreenElement ?? document.body }
  // Added to the tooltip's own (the arrow), e.g. to keep it inside a scroll container.
  if (popperModifiers) {
    popperProps.popperOptions = { modifiers: popperModifiers }
  }
  if (offsetX) {
    popperProps.style = { ...popperProps.style, marginLeft: -offsetX }
  }

  if (offsetY) {
    popperProps.style = { ...popperProps.style, marginTop: -offsetY }
  }

  const contentToRender = useMemo(() => content || <RichTooltipContent heading={heading} body={body} />, [content, heading, body])

  const tooltipClass = clsx(classes.tooltip, styles?.tooltip && styles?.tooltip)
  const arrowClass = clsx(classes.arrowOverride, styles?.arrow && styles?.arrow)
  return (
    disabled
      ? <>{ children }</>
      : <Tooltip
        classes={{ tooltip: tooltipClass, arrow: arrowClass, popper: classes.popper }}
        onOpen={onOpen}
        open={open}
        disableHoverListener={isControlled}
        disableFocusListener={isControlled}
        disableTouchListener={isControlled}
        placement={placement}
        slotProps={{
          popper: popperProps,
        }}
        arrow={!hideArrow}
        enterDelay={enterDelay}
        enterTouchDelay={enterTouchDelay}
        title={(
          <>
            <div className={classes.tooltipContainer}>
              <div className={clsx(classes.content, styles.content)}>
                {contentToRender}
              </div>
              <div className={classes.paddingBorder}></div>
            </div>
          </>
        )}>
        {children}
      </Tooltip>
  )
}

RichTooltip.propTypes = {
  children: PropTypes.any.isRequired,
  content: function(props, propName, componentName) {
    if (props.heading === undefined) {
      PropTypes.checkPropTypes(RichTooltip.propTypes, props, propName, componentName)
    }
  },
  placement: PropTypes.oneOf([Direction.BOTTOM, Direction.TOP, Direction.LEFT, Direction.RIGHT, Direction.TOP_END]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  onOpen: PropTypes.func,
  heading: PropTypes.string,
  body: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  styles: PropTypes.object,
  disabled: PropTypes.bool,
  enterDelay: PropTypes.number,
  enterTouchDelay: PropTypes.number,
  hideArrow: PropTypes.bool,
  open: PropTypes.bool,
  popperModifiers: PropTypes.arrayOf(PropTypes.object),
}

export default RichTooltip
